const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** Offset (ms) of an IANA zone from UTC at a given instant: localWallClock - utc. */
function tzOffsetMs(utcMs: number, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  const hour = map.hour === "24" ? 0 : Number(map.hour);
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    hour,
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - utcMs;
}

/** UTC epoch ms for a wall-clock time in an IANA zone (or "UTC"). DST-safe. */
function zonedWallClockToUtc(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  tz: string,
): number {
  if (tz === "UTC" || tz === "Etc/UTC") return Date.UTC(y, mo, d, h, mi);
  const naiveUtc = Date.UTC(y, mo, d, h, mi);
  const firstGuess = naiveUtc - tzOffsetMs(naiveUtc, tz);
  return naiveUtc - tzOffsetMs(firstGuess, tz);
}

/** The IANA zone the Claude CLI renders reset times in when the /usage panel
 *  omits the parenthetical: the machine's own local zone (the CLI formats the
 *  epoch `resets_at` in local time). On a Claudespace pod that is UTC, so the
 *  default is exact there; elsewhere it's off by at most the local↔account tz
 *  offset — still far better than a permanent "reset —". */
function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Parse the trailing "resets …" phrase of one /usage row into an absolute ISO
 * instant. Handles: "resets Jul 15 at 2am (America/Chicago)",
 * "Resets Jul 20, 7pm (UTC)", "Resets 4:40am (UTC)" (time-only session), and
 * the timezone-less forms the CLI renders when the reset is today —
 * "Resets 2pm", "Resets by 6:00am" — which fall back to `defaultTz`.
 * Year is inferred as the next occurrence at/after `now`. Best-effort — returns
 * null on any unrecognized shape (never throws).
 */
export function parseResetInstant(
  rowText: string,
  now: number,
  defaultTz: string = localTimeZone(),
): string | null {
  const m =
    /resets\s+(?:by\s+)?(?:([a-z]{3})\s+(\d{1,2})(?:,|\s+at)?\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)(?:\s*\(([^)]+)\))?/i.exec(
      rowText,
    );
  if (!m) return null;
  const [, monStr, dayStr, hourStr, minStr, meridiem, tzRaw] = m;
  const tz = tzRaw ? tzRaw.trim() : defaultTz;
  let hour = Number(hourStr) % 12;
  if (/pm/i.test(meridiem)) hour += 12;
  const minute = minStr ? Number(minStr) : 0;
  const nowDate = new Date(now);

  try {
    if (monStr && dayStr) {
      const month = MONTHS.indexOf(monStr.toLowerCase());
      if (month < 0) return null;
      const day = Number(dayStr);
      let utc = zonedWallClockToUtc(nowDate.getUTCFullYear(), month, day, hour, minute, tz);
      // Handle Dec→Jan rollover: a parsed date well in the past means next year.
      if (utc < now - 24 * 60 * 60 * 1000) {
        utc = zonedWallClockToUtc(nowDate.getUTCFullYear() + 1, month, day, hour, minute, tz);
      }
      return new Date(utc).toISOString();
    }

    // Time-only (session): next occurrence of this clock time in tz at/after now.
    let utc = zonedWallClockToUtc(
      nowDate.getUTCFullYear(),
      nowDate.getUTCMonth(),
      nowDate.getUTCDate(),
      hour,
      minute,
      tz,
    );
    if (utc <= now) {
      utc = zonedWallClockToUtc(
        nowDate.getUTCFullYear(),
        nowDate.getUTCMonth(),
        nowDate.getUTCDate() + 1,
        hour,
        minute,
        tz,
      );
    }
    return new Date(utc).toISOString();
  } catch {
    // Malformed/unknown timezone (e.g. non-IANA text captured verbatim from CLI
    // output) throws RangeError from Intl.DateTimeFormat. Best-effort: never throws.
    return null;
  }
}
